import { useRef, forwardRef, type ReactNode } from "react"
import { Table } from "@shared/components/ui/table"
import { cn } from "@shared/lib/utils"
import { useTableScroll } from "../hooks/use-table-scroll"
import { useScrollToActiveRow } from "../hooks/useScrollToActiveRow"
import { DataTableHeader } from "./DataTableHeader"
import { DataTableBody } from "./DataTableBody"
import type { Column, TableAction, TableActions } from "@shared/types/data-table"

interface SelectionState {
  isIndeterminate: boolean
  isAllSelected: boolean
}

interface DataTableContainerProps<T extends object> {
  data: T[]
  columns: Column<T>[]
  selectedItems: string[]
  unavailableItems: string[]
  rowActions?: TableActions<T> | ((item: T) => TableActions<T>)
  hoverActions?: TableAction<T>[] | ((item: T) => TableAction<T>[])
  onRowClick?: (item: T) => void
  onToggleSelection: (itemId: string) => void
  onToggleAllSelection: () => void
  getItemId: (item: T) => string
  getItemStatus?: (item: T) => string
  allowedStatuses: string[]
  selectionState: SelectionState
  loading: boolean
  activeItemId?: string
  className?: string
  children?: ReactNode
  /** Accessible label for the table element */
  tableAriaLabel?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRowClassName?: (item: any) => string | undefined
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DataTableContainer = forwardRef<HTMLDivElement, DataTableContainerProps<any>>(({
  data,
  columns,
  selectedItems,
  unavailableItems,
  rowActions,
  hoverActions,
  onRowClick,
  onToggleSelection,
  onToggleAllSelection,
  getItemId,
  getItemStatus,
  allowedStatuses,
  selectionState,
  loading,
  activeItemId,
  className,
  children,
  tableAriaLabel,
  getRowClassName,
// eslint-disable-next-line @typescript-eslint/no-explicit-any
}: DataTableContainerProps<any>, ref: React.Ref<HTMLDivElement>) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const { showRightShadow } = useTableScroll(scrollRef)
  
  const hasSelection = selectedItems !== undefined
  const hasActions = !!(rowActions || onRowClick)
  const hasData = !!(data?.length)
  
  // Add scroll-to-active-row functionality
  useScrollToActiveRow({
    activeItemId,
    disabled: loading || !hasData
  })

  return (
    <div
      ref={ref}
      className={cn("relative w-full flex flex-col flex-1 min-h-0", className)}
    >
      <div
        ref={scrollRef}
        className="relative flex-1 min-h-0 overflow-x-auto overflow-y-auto scroll-smooth"
      >
        <Table className="w-full table-fixed" aria-label={tableAriaLabel}>
            <DataTableHeader
              columns={columns}
              hasSelection={hasSelection}
              hasActions={hasActions}
              showRightShadow={showRightShadow}
              selectionState={selectionState}
              onToggleAllSelection={onToggleAllSelection}
            />

            <DataTableBody
              data={data}
              columns={columns}
              selectedItems={selectedItems}
              unavailableItems={unavailableItems}
              rowActions={rowActions}
              hoverActions={hoverActions}
              onRowClick={onRowClick}
              onToggleSelection={onToggleSelection}
              showRightShadow={showRightShadow}
              getItemId={getItemId}
              getItemStatus={getItemStatus}
              allowedStatuses={allowedStatuses}
              activeItemId={activeItemId}
              getRowClassName={getRowClassName}
            />
          </Table>
        </div>
        {children}
      </div>
  )
})

DataTableContainer.displayName = 'DataTableContainer'